/**
 * Custom Svelte transitions and animations
 * Provides smooth, polished UI interactions
 */

import { cubicOut, elasticOut, bounceOut } from 'svelte/easing';
import type { TransitionConfig } from 'svelte/transition';

/**
 * Enhanced slide transition with bounce
 */
export function slideIn(
  node: HTMLElement,
  { delay = 0, duration = 400, axis = 'y' }: { delay?: number; duration?: number; axis?: 'x' | 'y' } = {}
): TransitionConfig {
  const style = getComputedStyle(node);
  const opacity = +style.opacity;
  const primaryDimension = axis === 'y' ? 'height' : 'width';
  const primaryTransform = axis === 'y' ? 'translateY' : 'translateX';

  return {
    delay,
    duration,
    easing: cubicOut,
    css: (t) => {
      const eased = cubicOut(t);
      return `
        opacity: ${eased * opacity};
        transform: ${primaryTransform}(${(1 - eased) * 20}px);
      `;
    },
  };
}

/**
 * Scale with bounce effect
 */
export function scaleBounce(
  node: HTMLElement,
  { delay = 0, duration = 500, start = 0 }: { delay?: number; duration?: number; start?: number } = {}
): TransitionConfig {
  return {
    delay,
    duration,
    easing: elasticOut,
    css: (t) => {
      const eased = elasticOut(t);
      return `
        transform: scale(${start + (1 - start) * eased});
        opacity: ${t};
      `;
    },
  };
}

/**
 * Shake animation for errors
 */
export function shake(
  node: HTMLElement,
  { duration = 500 }: { duration?: number } = {}
): TransitionConfig {
  return {
    duration,
    css: (t) => {
      const x = Math.sin(t * Math.PI * 4) * 10 * (1 - t);
      return `transform: translateX(${x}px);`;
    },
  };
}

/**
 * Flip card transition
 */
export function flip3d(
  node: HTMLElement,
  { delay = 0, duration = 600, axis = 'y' }: { delay?: number; duration?: number; axis?: 'x' | 'y' } = {}
): TransitionConfig {
  const style = getComputedStyle(node);
  const opacity = +style.opacity;

  return {
    delay,
    duration,
    easing: cubicOut,
    css: (t) => {
      const eased = cubicOut(t);
      const rotation = (1 - eased) * 180;
      const rotateAxis = axis === 'y' ? 'rotateY' : 'rotateX';

      return `
        opacity: ${t < 0.5 ? 0 : (t - 0.5) * 2 * opacity};
        transform: ${rotateAxis}(${rotation}deg);
        backface-visibility: hidden;
      `;
    },
  };
}

/**
 * Fade with scale
 */
export function fadeScale(
  node: HTMLElement,
  { delay = 0, duration = 300, baseScale = 0.95 }: { delay?: number; duration?: number; baseScale?: number } = {}
): TransitionConfig {
  return {
    delay,
    duration,
    easing: cubicOut,
    css: (t) => {
      const eased = cubicOut(t);
      return `
        opacity: ${eased};
        transform: scale(${baseScale + (1 - baseScale) * eased});
      `;
    },
  };
}

/**
 * Blur transition
 */
export function blurFade(
  node: HTMLElement,
  { delay = 0, duration = 300, amount = 10 }: { delay?: number; duration?: number; amount?: number } = {}
): TransitionConfig {
  return {
    delay,
    duration,
    easing: cubicOut,
    css: (t) => {
      const eased = cubicOut(t);
      return `
        opacity: ${eased};
        filter: blur(${(1 - eased) * amount}px);
      `;
    },
  };
}

/**
 * Bounce in animation
 */
export function bounceIn(
  node: HTMLElement,
  { delay = 0, duration = 600 }: { delay?: number; duration?: number } = {}
): TransitionConfig {
  return {
    delay,
    duration,
    easing: bounceOut,
    css: (t) => {
      const eased = bounceOut(t);
      return `
        opacity: ${t};
        transform: scale(${eased}) translateY(${(1 - eased) * -50}px);
      `;
    },
  };
}

/**
 * Stagger children animations
 */
export function staggered(
  node: HTMLElement,
  { delay = 0, duration = 400, stagger = 50 }: { delay?: number; duration?: number; stagger?: number } = {}
): TransitionConfig {
  const children = Array.from(node.children) as HTMLElement[];

  children.forEach((child, i) => {
    child.style.opacity = '0';
    child.style.transform = 'translateY(20px)';

    setTimeout(() => {
      child.style.transition = `opacity ${duration}ms, transform ${duration}ms`;
      child.style.opacity = '1';
      child.style.transform = 'translateY(0)';
    }, delay + i * stagger);
  });

  return {
    delay: 0,
    duration: delay + children.length * stagger + duration,
    css: () => '',
  };
}

/**
 * CSS Animation classes for Tailwind
 */
export const animations = {
  // Pulse
  pulse: 'animate-pulse',

  // Bounce
  bounce: 'animate-bounce',

  // Spin
  spin: 'animate-spin',

  // Ping
  ping: 'animate-ping',

  // Custom animations (add to tailwind.config.js)
  wiggle: 'animate-wiggle',
  shake: 'animate-shake',
  fadeIn: 'animate-fade-in',
  slideUp: 'animate-slide-up',
  scaleIn: 'animate-scale-in',
};

/**
 * Trigger haptic feedback (mobile)
 */
export async function hapticFeedback(type: 'light' | 'medium' | 'heavy' = 'light') {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');

    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };

    await Haptics.impact({ style: styleMap[type] });
  } catch (error) {
    // Haptics not available (web)
    console.debug('Haptics not available');
  }
}

/**
 * Vibration pattern for errors
 */
export async function errorVibration() {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    await Haptics.vibrate({ duration: 100 });
    setTimeout(async () => {
      await Haptics.vibrate({ duration: 100 });
    }, 150);
  } catch (error) {
    console.debug('Haptics not available');
  }
}

/**
 * Success vibration pattern
 */
export async function successVibration() {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (error) {
    console.debug('Haptics not available');
  }
}
